<template>
  <div
    class="bubble"
    :dir="isRTLLanguage ? 'rtl' : 'ltr'"
    @mouseenter="handleMouseEnter"
    @mouseleave="showMenu = false"
    @contextmenu.prevent="copyText"
  >
    <div
      v-for="content in contents"
      :key="getKey(content)"
      :class="{
        'cursor-pointer': isReceivedAudio,
        'sound-playing': isPlayingAudio,
        'highlight-origin': content.highlight,
        'highlight-origin-fadeout': content.fadeout,
      }"
      class="sentence"
      :dir="isRTLLanguage ? 'rtl' : 'ltr'"
      :style="sentenceStyle"
      @click="emit('playAudioManual', isPlayingAudio, message)"
    >
      <template v-if="content.segments && content.segments.length">
        <p
          v-for="(segment, segmentIndex) in content.segments"
          :key="`segment_${segmentIndex}`"
          :class="{
            'complete-segment': segment.status === 'COMPLETED',
            'incomplete-segment': segment.status === 'INCOMPLETED',
            'new-line-segment': isNewLine(content.segments, segment),
          }"
        >
          <span v-if="segment.detectedLanguageCode" class="pc-badge" :class="getBadgeClass(segment.detectedLanguageCode)">
            {{ t(`label_language_${segment.detectedLanguageCode}`) }}
          </span>
          <pre v-if="segment.status === 'COMPLETED'" class="pre-segment">{{ segment.text }}</pre>
          <AnimationTyping
            :is-visible="segment.status === 'INCOMPLETED'"
            :text="segment.text"
            :dir="isRTLLanguage ? 'rtl' : 'ltr'"
            :is-keep-all="!isLanguageNoSpace(content.languageCode)"
            is-using-new-line
          />
        </p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, provide, ref, toRef, watch, CSSProperties } from 'vue';

import AnimationTyping from '@/components/animations/AnimationTyping.vue';
import { useMessageBubbleCommon } from '@/composables/domains/chat/useMessageBubbleCommon';
import { useMessageTextCommon } from '@/composables/domains/chat/useMessageTextCommon';
import { useAudioStore } from '@/stores/audio';
import { useUserStore } from '@/stores/user';
import { LanguageCode } from '@/types/common';
import { ChatSegment } from '@/types/socket/message';
import { ChatMessage, MessageText } from '@/types/socket.js';
import { isLanguageNoSpace } from '@/utils/chat/language';
import { RTL_LANGUAGES } from '@/utils/constants/language';
import { getBrowserAndDeviceInfo } from '@/utils/device';
import { ij_callLogId, ij_isLastIsOwn, ij_setEmptyBubbleMy } from '@/utils/injectionKeys.js';

const props = withDefaults(
  defineProps<{
    messages: ChatMessage[];
    message: ChatMessage;
    lastCallLogId?: string;
    detectedLanguageCodes: LanguageCode[];
  }>(),
  {
    messages: () => [],
    message: () => ({}) as ChatMessage,
    detectedLanguageCodes: () => [],
    longVoice: false,
  }
);

const { t } = useI18n();

const audioStore = useAudioStore();
const userStore = useUserStore();

const setEmptyBubbleMy = inject(ij_setEmptyBubbleMy, () => {});

const contentContainer = ref<HTMLElement>();
const isHidden = ref<boolean>(false);
const showMenu = ref<boolean>(false);

const emit = defineEmits<{ playAudioManual: [isPlayingAudio: boolean, message: ChatMessage] }>();

const { shrinkBubble, getKey } = useMessageTextCommon({ contentContainer });

const { handleChangeReset } = useMessageBubbleCommon({
  chatMessage: toRef(props, 'message'),
  setEmptyBubble: setEmptyBubbleMy,
  isHidden,
});

const isRTLLanguage = computed<boolean>(() => {
  return RTL_LANGUAGES.some((lang) => props.message.tgtLanguageSet.has(lang));
});

const isNewLine = (segments: ChatSegment[], segment: ChatSegment): boolean => {
  if (segment.status === 'COMPLETED') {
    const completeSegments = segments.filter(seg => seg.status === 'COMPLETED');
    const completeIndex = completeSegments.indexOf(segment);

    return completeSegments.length > completeIndex + 1 || !segments.some(segment => segment.status === 'INCOMPLETED');
  }

  if (segment.status === 'INCOMPLETED') {
    return !!segment.detectedLanguageCode;
  }

  return false;
}

const contents = computed<MessageText[]>(() => {
  const { tgt: { contents = [] } = {} } = props.message;
  return contents;
});

// 나의 발화 중 이 메세지가 최신인지
const isLastMessage = computed<boolean>(() => {
  const messageLastIsOwn = props.messages.at(-1);
  if (!messageLastIsOwn) return false;
  return messageLastIsOwn.callLogId === props.message.callLogId;
});

// 오디오 수신 여부
const isReceivedAudio = computed<boolean>(() => {
  if (!isLastIsOwn.value) return true;
  const isAudioComplete = props.message.isAudioComplete === true;
  const isReset = props.message.reset === '1';
  return isAudioComplete || isReset;
});

const isPlayingAudio = computed<boolean>(() => {
  return audioStore.playState == 2 && audioStore.playCallLogId === props.message.callLogId;
});

const isLastIsOwn = computed<boolean>(() => {
  const lastMessage = props.messages.findLast((item) => item.isOwn) || {};
  return lastMessage?.callLogId === props.message.callLogId;
});

const sentenceStyle = computed<CSSProperties>(() => {
  return {
    wordBreak: isEveryLanguageSpaced.value ? 'keep-all' : 'break-all',
    '--font-multiplier': String(userStore.userPreferences.textSizeMultiplier),
  };
});

const isEveryLanguageSpaced = computed<boolean>(() => {
  return [...props.message.tgtLanguageSet].every((code) => !isLanguageNoSpace(code));
});

const getBadgeClass = (code: string) => {
  const index = props.detectedLanguageCodes.indexOf(code);

  const sizeClass = userStore.userPreferences.textSizeMultiplier > 1.25 ? 'big-badge' : '';
  const speakerClass = index !== -1 && index <= 4 ? `speaker0${index + 1}` : 'speaker06'
  return [sizeClass, speakerClass];
};

const handleMouseEnter = () => {
  const { os } = getBrowserAndDeviceInfo();
  showMenu.value = os !== 'iOS';
};

const copyText = () => {
  if (!document.hasFocus()) {
    console.warn('Document not focused — copy aborted');
    return;
  }
  const contentsMapped = props.message.tgt.contents.map((item) => {
    if (!item.highlight) return item.content;
  });
  const textTotal = contentsMapped.join('');
  navigator.clipboard.writeText(textTotal);
};

watch(
  () => props.message.reset,
  (resetNew) => {
    handleChangeReset(resetNew);
  },
  { immediate: true }
);

// 글자 크기 변경 시 높이 조절
watch(
  () => userStore.userPreferences.textSizeMultiplier,
  () => {
    shrinkBubble(); // 버블 줄이기
  }
);

// 내가 더이상 마지막 상대 발화가 아니고 버블에 공백이 있을 때 버블 줄이기 발동
watch(
  isLastMessage,
  (newVal) => {
    if (!newVal) {
      shrinkBubble(); // 버블 줄이기
    }
  },
  { flush: 'post' }
);

provide(ij_callLogId, props.message.callLogId);
provide(ij_isLastIsOwn, isLastIsOwn);
</script>

<style scoped lang="scss">
.loading {
  width: fit-content;
  align-self: flex-end;
  margin-bottom: 0.75rem;
  padding: 0.25rem 1.5rem 0.75rem 1rem;
  border-radius: 8px;
}

.bubble {
  position: relative;
  min-height: unset;
  padding: 0 1rem;
  margin-bottom: 1rem;

  .sentence {
    width: 100%;
    height: fit-content;
    flex-shrink: 0;
    transition:
      opacity 0.4s ease,
      transform 0.4s ease;
    text-align: start;
    line-height: 160%;
    color: var(--font-default);
    border-radius: 4px;
    unicode-bidi: isolate;

    &.cursor-pointer {
      cursor: pointer;
    }

    .complete-segment,
    .incomplete-segment {
      display: inline;
      font-size: calc($font-size-p * var(--font-multiplier, 1));

      &:last-child {
        margin-bottom: 0;
      }

      &.new-line-segment {
        display: block;
      }
    }

    .complete-segment {
      animation: fadeIn 1s ease-in-out forwards;
    }

    .incomplete-segment {
      opacity: 0.5;
      animation: none;
    }

    .pc-badge {
      font-size: clamp(0.75rem, calc(1rem * (var(--font-multiplier, 1) * 0.375 + 0.5)), 1.25rem);
      margin-right: $spacing1;

      &.big-badge {  
        vertical-align: 3px;  
      }
    }
  }
}

/* 오리지널 - 소리 Play */
.sound-playing {
  color: var(--font-default);
}

/* 오리지널 - 통역/입력중인 텍스트 */
.highlight-origin {
  color: var(--font-default);
  animation: none;
}

/* 오리지널 - 통역 완료된 텍스트 */
.highlight-origin-fadeout {
  display: inline;
  color: var(--font-default);
  animation: fadeIn 3s ease-in-out forwards;
}

.pre-segment {
  white-space: pre-line;
  font-family: inherit;
  font-size: inherit;
  display: inline;
  overflow-wrap: anywhere;
  margin: 0;
  padding: 0;
}

@keyframes fadeIn {
  0% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
</style>
